import React, { useState, useEffect, useCallback } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth, API } from '../context/AuthContext';
import axios from 'axios';
import {
  Package, FileText, LogOut, User, ChevronRight, Plus,
  Clock, CheckCircle, XCircle, ShoppingBag, Layers, Brush,
  AlertCircle, ArrowRight
} from 'lucide-react';

const statusConfig = {
  en_attente: { label: 'En attente', color: 'bg-yellow-100 text-yellow-700', icon: Clock },
  en_fabrication: { label: 'En fabrication', color: 'bg-blue-100 text-blue-700', icon: Package },
  expediee: { label: 'Expédiée', color: 'bg-orange-100 text-orange-600', icon: ChevronRight },
  livree: { label: 'Livrée', color: 'bg-green-100 text-green-700', icon: CheckCircle },
  annulee: { label: 'Annulée', color: 'bg-red-100 text-red-700', icon: XCircle },
};

const DashboardPage = () => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [orders, setOrders] = useState([]);
  const [loadingOrders, setLoadingOrders] = useState(true);
  const [activeTab, setActiveTab] = useState('dashboard');

  const fetchOrders = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/pro/orders`, { withCredentials: true });
      setOrders(res.data);
    } catch (e) {
      console.error('Error fetching orders', e);
    } finally {
      setLoadingOrders(false);
    }
  }, []);

  useEffect(() => {
    fetchOrders();
  }, [fetchOrders]);

  const handleLogout = async () => {
    await logout();
    navigate('/');
  };

  const pendingOrders = orders.filter(o => o.status === 'en_attente');
  const completedOrders = orders.filter(o => o.status === 'livree');

  const tabs = [
    { id: 'dashboard', label: 'Tableau de bord', icon: Layers },
    { id: 'orders', label: 'Mes commandes', icon: Package },
    { id: 'account', label: 'Mon compte', icon: User },
  ];

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-[#1a1a1a] py-5">
        <div className="max-w-7xl mx-auto px-4 flex items-center justify-between">
          <div>
            <Link to="/" className="text-xl font-black text-white">
              Stores <span className="text-orange-500">&</span> Pergolas
            </Link>
            <p className="text-gray-400 text-xs mt-0.5">Espace Professionnel</p>
          </div>
          <div className="flex items-center gap-4">
            <div className="text-right hidden sm:block">
              <p className="text-white text-sm font-semibold">{user?.prenom} {user?.nom}</p>
              <p className="text-gray-400 text-xs">{user?.company_name}</p>
            </div>
            <button
              onClick={handleLogout}
              className="flex items-center gap-2 text-gray-400 hover:text-white transition-colors text-sm"
            >
              <LogOut size={16} /> Déconnexion
            </button>
          </div>
        </div>
      </div>

      {/* Account Pending Warning */}
      {user?.status === 'pending' && (
        <div className="bg-yellow-50 border-b border-yellow-200">
          <div className="max-w-7xl mx-auto px-4 py-3 flex items-center gap-3">
            <AlertCircle size={18} className="text-yellow-600 flex-shrink-0" />
            <p className="text-yellow-800 text-sm">
              <strong>Compte en attente de validation</strong> — Notre équipe va valider votre compte sous 24h. Vous pourrez passer commande après validation.
            </p>
          </div>
        </div>
      )}

      <div className="max-w-7xl mx-auto px-4 py-8">
        <div className="flex flex-col lg:flex-row gap-6">
          {/* Sidebar */}
          <aside className="lg:w-64 flex-shrink-0">
            <div className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden">
              {/* Profile */}
              <div className="p-5 border-b border-gray-100">
                <div className="w-12 h-12 bg-orange-100 rounded-full flex items-center justify-center mb-3">
                  {user?.picture ? (
                    <img src={user.picture} alt="" className="w-12 h-12 rounded-full object-cover" />
                  ) : (
                    <span className="text-orange-600 font-black text-lg">{(user?.prenom || user?.name || 'U')[0].toUpperCase()}</span>
                  )}
                </div>
                <p className="font-bold text-gray-900 text-sm">{user?.prenom} {user?.nom}</p>
                <p className="text-gray-500 text-xs">{user?.company_name}</p>
                <span className={`inline-block mt-2 text-xs px-2 py-0.5 rounded-full font-semibold ${
                  user?.status === 'approved' ? 'bg-green-100 text-green-700' :
                  user?.status === 'pending' ? 'bg-yellow-100 text-yellow-700' :
                  'bg-red-100 text-red-700'
                }`}>
                  {user?.status === 'approved' ? 'Compte validé' : user?.status === 'pending' ? 'En attente' : 'Refusé'}
                </span>
              </div>
              {/* Nav */}
              <nav className="p-2">
                {tabs.map(tab => {
                  const Icon = tab.icon;
                  return (
                    <button
                      key={tab.id}
                      onClick={() => setActiveTab(tab.id)}
                      className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-all ${
                        activeTab === tab.id ? 'bg-orange-50 text-orange-600' : 'text-gray-600 hover:bg-gray-50'
                      }`}
                    >
                      <Icon size={16} />
                      {tab.label}
                    </button>
                  );
                })}
                <div className="border-t border-gray-100 mt-2 pt-2">
                  <Link
                    to="/toiles-sur-mesure"
                    className="w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium text-gray-600 hover:bg-orange-50 hover:text-orange-600 transition-all"
                  >
                    <Layers size={16} /> Commander des toiles
                  </Link>
                  <Link
                    to="/marquage"
                    className="w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium text-gray-600 hover:bg-orange-50 hover:text-orange-600 transition-all"
                  >
                    <Brush size={16} /> Marquage lambrequin
                  </Link>
                </div>
              </nav>
            </div>
          </aside>

          {/* Main content */}
          <main className="flex-1">
            {/* DASHBOARD TAB */}
            {activeTab === 'dashboard' && (
              <div>
                <h1 className="text-2xl font-black text-gray-900 mb-6">Bienvenue, {user?.prenom || user?.name} !</h1>

                {/* Stats */}
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-8">
                  {[
                    { label: 'Commandes totales', value: orders.length, icon: Package, color: 'bg-blue-50 text-blue-600' },
                    { label: 'En attente', value: pendingOrders.length, icon: Clock, color: 'bg-yellow-50 text-yellow-600' },
                    { label: 'Livrées', value: completedOrders.length, icon: CheckCircle, color: 'bg-green-50 text-green-600' },
                  ].map((stat, i) => {
                    const Icon = stat.icon;
                    return (
                      <div key={i} className="bg-white rounded-xl p-5 border border-gray-100 shadow-sm">
                        <div className={`w-10 h-10 rounded-lg flex items-center justify-center mb-3 ${stat.color}`}>
                          <Icon size={20} />
                        </div>
                        <p className="text-2xl font-black text-gray-900">{stat.value}</p>
                        <p className="text-gray-500 text-sm">{stat.label}</p>
                      </div>
                    );
                  })}
                </div>

                {/* Quick Actions */}
                <h2 className="text-lg font-black text-gray-900 mb-4">Passer une commande</h2>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-8">
                  {[
                    { icon: ShoppingBag, title: 'Store complet', desc: 'Stores bannes, pergolas sur mesure', link: '/nos-stores', color: 'bg-orange-500' },
                    { icon: Layers, title: 'Toile sur mesure', desc: 'Catalogue Dickson, Sauleda, Sattler...', link: '/toiles-sur-mesure', color: 'bg-blue-500' },
                    { icon: Brush, title: 'Marquage lambrequin', desc: 'Flocage texte + logo sur lambrequin', link: '/marquage', color: 'bg-purple-500' },
                  ].map((action, i) => {
                    const Icon = action.icon;
                    return (
                      <Link
                        key={i}
                        to={user?.status === 'approved' ? action.link : '#'}
                        className={`bg-white rounded-xl p-5 border border-gray-100 shadow-sm hover:shadow-md transition-all group ${
                          user?.status !== 'approved' ? 'opacity-60 cursor-not-allowed' : ''
                        }`}
                      >
                        <div className={`w-10 h-10 ${action.color} rounded-lg flex items-center justify-center mb-3 group-hover:scale-110 transition-transform`}>
                          <Icon size={20} className="text-white" />
                        </div>
                        <h3 className="font-bold text-gray-900 mb-1">{action.title}</h3>
                        <p className="text-gray-500 text-xs">{action.desc}</p>
                        {user?.status === 'approved' && (
                          <div className="flex items-center gap-1 mt-3 text-orange-500 text-xs font-semibold">
                            Commander <ArrowRight size={12} />
                          </div>
                        )}
                      </Link>
                    );
                  })}
                </div>

                {/* Recent Orders */}
                <h2 className="text-lg font-black text-gray-900 mb-4">Commandes récentes</h2>
                {loadingOrders ? (
                  <div className="space-y-3">
                    {[1,2,3].map(i => <div key={i} className="bg-gray-100 h-16 rounded-xl animate-pulse" />)}
                  </div>
                ) : orders.length === 0 ? (
                  <div className="bg-white rounded-xl border border-gray-100 p-8 text-center">
                    <Package size={32} className="text-gray-300 mx-auto mb-3" />
                    <p className="text-gray-500">Aucune commande pour le moment</p>
                    <Link
                      to={user?.status === 'approved' ? '/toiles-sur-mesure' : '#'}
                      className="inline-flex items-center gap-2 mt-4 text-orange-500 font-semibold text-sm hover:gap-3 transition-all"
                    >
                      Passer ma première commande <ArrowRight size={14} />
                    </Link>
                  </div>
                ) : (
                  <div className="space-y-3">
                    {orders.slice(0, 5).map(order => {
                      const sc = statusConfig[order.status] || statusConfig.en_attente;
                      const Icon = sc.icon;
                      return (
                        <div key={order.order_id} className="bg-white rounded-xl border border-gray-100 p-4 flex items-center justify-between">
                          <div>
                            <p className="font-bold text-gray-900 text-sm">{order.order_id}</p>
                            <p className="text-gray-400 text-xs">{order.items?.length} article(s) • {new Date(order.created_at).toLocaleDateString('fr-FR')}</p>
                          </div>
                          <div className="flex items-center gap-3">
                            <span className="font-bold text-gray-900 text-sm">{order.total_ht?.toFixed(2)} € HT</span>
                            <span className={`flex items-center gap-1 text-xs font-semibold px-2.5 py-1 rounded-full ${sc.color}`}>
                              <Icon size={12} /> {sc.label}
                            </span>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            )}

            {/* ORDERS TAB */}
            {activeTab === 'orders' && (
              <div>
                <div className="flex items-center justify-between mb-6">
                  <h1 className="text-2xl font-black text-gray-900">Mes Commandes</h1>
                </div>
                {loadingOrders ? (
                  <div className="space-y-3">{[1,2,3].map(i => <div key={i} className="bg-gray-100 h-24 rounded-xl animate-pulse" />)}</div>
                ) : orders.length === 0 ? (
                  <div className="bg-white rounded-xl border border-gray-100 p-12 text-center">
                    <Package size={40} className="text-gray-300 mx-auto mb-4" />
                    <h3 className="font-bold text-gray-700 mb-2">Aucune commande</h3>
                    <p className="text-gray-400 text-sm">Vous n'avez pas encore passé de commande.</p>
                  </div>
                ) : (
                  <div className="space-y-4">
                    {orders.map(order => {
                      const sc = statusConfig[order.status] || statusConfig.en_attente;
                      const Icon = sc.icon;
                      return (
                        <div key={order.order_id} className="bg-white rounded-xl border border-gray-100 p-5">
                          <div className="flex items-start justify-between mb-3">
                            <div>
                              <p className="font-black text-gray-900">{order.order_id}</p>
                              <p className="text-gray-400 text-xs mt-0.5">{new Date(order.created_at).toLocaleDateString('fr-FR', { day: 'numeric', month: 'long', year: 'numeric' })}</p>
                            </div>
                            <span className={`flex items-center gap-1.5 text-xs font-semibold px-3 py-1.5 rounded-full ${sc.color}`}>
                              <Icon size={12} /> {sc.label}
                            </span>
                          </div>
                          <div className="border-t border-gray-100 pt-3">
                            {order.items?.map((item, i) => (
                              <div key={i} className="flex items-center justify-between py-1.5 text-sm">
                                <span className="text-gray-700">{item.product_name} ×{item.quantity}</span>
                                <span className="font-semibold text-gray-900">{item.total_price?.toFixed(2)} €</span>
                              </div>
                            ))}
                          </div>
                          <div className="border-t border-gray-100 pt-3 flex items-center justify-between">
                            <p className="text-sm text-gray-500">{order.delivery_address}</p>
                            <p className="font-black text-gray-900">Total: {order.total_ht?.toFixed(2)} € HT</p>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            )}

            {/* ACCOUNT TAB */}
            {activeTab === 'account' && (
              <div>
                <h1 className="text-2xl font-black text-gray-900 mb-6">Mon Compte</h1>
                <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-6">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                    {[
                      { label: 'Prénom', value: user?.prenom || user?.name?.split(' ')[0] },
                      { label: 'Nom', value: user?.nom || user?.name?.split(' ').slice(1).join(' ') },
                      { label: 'Email', value: user?.email },
                      { label: 'Téléphone', value: user?.phone || '—' },
                      { label: 'Société', value: user?.company_name || '—' },
                      { label: 'SIRET', value: user?.siret || '—' },
                      { label: 'Activité', value: user?.activity || '—' },
                      { label: 'Adresse', value: user?.address ? `${user.address}, ${user.postal_code} ${user.city}` : '—' },
                    ].map(({ label, value }) => (
                      <div key={label}>
                        <p className="text-xs text-gray-400 uppercase tracking-wider mb-1">{label}</p>
                        <p className="font-semibold text-gray-800">{value}</p>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            )}
          </main>
        </div>
      </div>
    </div>
  );
};

export default DashboardPage;
